import React, {useCallback, useState} from 'react';

import {
  View,
  Linking,
  StyleSheet,
  Pressable,
  Platform,
} from 'react-native';

import {UserInfo} from '../../types';
import {useMutation, useQuery} from 'react-query';
import {
  ActivityIndicator,
  List,
  Divider,
  Text,
  Chip,
  useTheme,
  TextInput,
} from 'react-native-paper';
import {
  NavigationProp,
  useNavigation,
} from '@react-navigation/native';
import {getUserData, writeItem} from '../../api';
import {SettingsStackParams} from '../../types/nav';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setIsEditingProfile} from '../../store/slices/ui';
import {SettingsAvatar} from './avatar';

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flexDirection: 'column',
  },
  customTitle: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  additionalPadding: {
    paddingTop: 8,
  },
  image: {
    width: '100%',
    height: 'auto',
    opacity: 0.6,
    position: 'absolute',
    resizeMode: 'cover',
    zIndex: -1,
    flex: 1,
  },
});
const SettingsView = () => {
  const userDataQuery = useQuery('UserData', () => getUserData());
  const isLoadingUserData = userDataQuery.isLoading;
  const nav = useNavigation<NavigationProp<SettingsStackParams>>();
  const UserData = userDataQuery.data;
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector(
    state => state.ui.isEditingProfile,
  );
  const openDevices = () => nav.navigate('Devices');
  const openDbs = () => nav.navigate('Databases');
  const openPlus = () => nav.navigate('Premium');
  const showDiskStatus = () => nav.navigate('Drive');
  const [userName, setUserName] = useState('');
  const [userDescription, setUserDescription] = useState('');
  const onEditProfile = () => {
    dispatch(setIsEditingProfile(true));
    setUserName(UserData?.name as string);
    setUserDescription(UserData?.description as string);
  };
  const writeProfileMutation = useMutation(async () => {
    const nuser = UserData as UserInfo;
    nuser.name = userName;
    nuser.description = userDescription;
    await writeItem(nuser);
  });
  const onSaveProfile = () => {
    writeProfileMutation.mutateAsync().then(async () => {
      await userDataQuery.refetch();
      dispatch(setIsEditingProfile(false));
    });
  };
  const editIcon = isEditing ? 'check-outline' : 'pencil-outline';
  const AvatarSize = 72;
  const controlIconPaddingTop = 20;
  const touchableStyle = {
    pointerEvents: 'auto',
    paddingTop: controlIconPaddingTop,
  } as any;
  const theme = useTheme();
  return (
    <View
      style={{
        height: '100%',
        width: '100%',
      }}>
      {isLoadingUserData ? (
        <ActivityIndicator animating={true} size={72} />
      ) : (
        <>
          <List.Section>
            <List.Item
              theme={theme}
              title={isEditing ? null : UserData?.name}
              style={{height: isEditing ? 160 : 'auto'}}
              left={() => (
                <SettingsAvatar
                  AvatarSize={AvatarSize}
                  UserData={UserData}
                  style={{marginLeft: 20}}
                />
              )}
              description={({
                ellipsizeMode,
                color: descriptionColor,
                fontSize,
              }) => (
                <View style={[styles.container, styles.column]}>
                  {!isEditing ? (
                    <Text
                      theme={theme}
                      numberOfLines={1}
                      ellipsizeMode={ellipsizeMode}
                      style={{color: descriptionColor, fontSize}}>
                      {UserData?.description}
                    </Text>
                  ) : (
                    <>
                      <TextInput
                        theme={theme}
                        mode="outlined"
                        onChangeText={setUserName}
                        value={userName}
                      />
                      <TextInput
                        theme={theme}
                        mode="outlined"
                        onChangeText={setUserDescription}
                        value={userDescription}
                      />
                    </>
                  )}
                  <View
                    style={[
                      styles.container,
                      styles.row,
                      styles.additionalPadding,
                    ]}>
                    {Platform.OS == 'macos' ? (
                      <Chip
                        icon="laptop"
                        onPress={() => {}}
                        style={{height: 30}}>
                        MacOS
                      </Chip>
                    ) : null}
                    {Platform.OS === 'web' ? (
                      <Chip
                        icon="web"
                        onPress={() => {}}
                        style={{height: 30}}>
                        WEB
                      </Chip>
                    ) : null}
                  </View>
                </View>
              )}
              right={props => (
                <Pressable
                  style={touchableStyle}
                  onPress={isEditing ? onSaveProfile : onEditProfile}>
                  <List.Icon {...props} icon={editIcon} />
                </Pressable>
              )}
            />
            {/* <Divider />
            <List.Item
              onPress={showDiskStatus}
              title={'Drive Status'}
              left={props => (
                <List.Icon icon="harddisk" style={props.style} />
              )}
            /> */}
            <Divider />
            <List.Item
              onPress={openDevices}
              title={'Devices'}
              left={props => (
                <List.Icon icon="devices" style={props.style} />
              )}
            />
            <Divider />
            <List.Item
              onPress={openDbs}
              title={'Databases'}
              left={props => (
                <List.Icon icon="database" style={props.style} />
              )}
            />
            <Divider />
            {Platform.OS === 'web' && (
              <>
                <List.Item
                  onPress={openPlus}
                  title={'Premium Features'}
                  left={props => (
                    <List.Icon
                      icon="plus-circle"
                      style={props.style}
                    />
                  )}
                />
                <Divider />
              </>
            )}
          </List.Section>
        </>
      )}
    </View>
  );
};

export default SettingsView;
